/**
 * The app-side binding of `EvidenceAnswerPorts` (ruling 62). The host that mounts
 * it is the sibling module — a file exporting a hook AND a component fails the
 * `react-refresh/only-export-components` ratchet, whose baseline is 0.
 *
 * ELEVEN OF THE THIRTEEN MEMBERS ARE BOUND HERE. The other two —
 * `getPartnerConfig` and `sourceActionsComponent` — resolve to modules ruling 62
 * forbids moving (`src/components/chat/constants/integrationPartners.ts`, and
 * `SourceCardActions` with its twelve-file feedback subgraph), and both are
 * inside the Chat quarantine globs. A file outside Chat naming either is a NEW
 * inbound pair on a set that is shrink-only in both directions, so they arrive
 * through the slot below instead: Chat owns them, Chat fills the slot, and no
 * composition point has to reach into Chat for them.
 *
 * WHEN THE SLOT IS FILLED. `src/components/chat/components/evidence-answer-ports.ts`
 * calls the registrar at module scope, and `components.tsx` imports it. Every
 * path that can render an answer surface reaches that module statically —
 * Chat's own timeline directly, and the Projects timeline through
 * `renderProjectItem.tsx` -> `EvidenceSessionInlineBlock` -> `EvidenceTimelineBody`
 * -> `components.tsx`. An unfilled slot throws by name rather than rendering a
 * degraded surface.
 */

import { viewingSourceAtom } from '@/atoms/sourceViewerAtoms';
import { useEvidenceAnalytics } from '@/hooks/useEvidenceAnalytics';
import { track } from '@/lib/analytics/track';
import { TrackingEventName } from '@/models/analytics/eventTypes';
import { isAuthenticatedSourceUrl } from '@/utils/authenticatedSourceUrl';
import { triggerBrowserPrint } from '@/utils/browserPrint';
import { PUBLIC_ROUTES } from '@/utils/publicRoutes';
import { documentPreviewAtom, type EvidenceAnswerPorts } from '@heidi/evidence';
import type { EvidenceAnswerEvent } from '@heidi/evidence/contracts';
import { getPartnerFigureUrl, markSourceReadByUrl } from '@heidi/evidence/data';
import { ButtonAction } from '@heidi/runtime/analytics-vocabulary';
import { useSetAtom } from 'jotai';
import { useRouter } from 'next/router';
import { useMemo } from 'react';

import { CitationOcrQualityTag } from './citation-ocr-quality-tag';
import { resolveEvidenceAnswerChatPorts } from './evidence-answer-chat-ports';

export type EvidenceSourceOpeners = Pick<
  EvidenceAnswerPorts,
  | 'openInViewer'
  | 'openLibraryDocument'
  | 'openInNewTab'
  | 'trackSourceClick'
  | 'isAuthenticatedUrl'
>;

/**
 * The members that open a source and report the click: what a source list
 * needs, without the answer's analytics subscriptions. Bind it once where the
 * list's owner is composed.
 */
export const useEvidenceSourceOpeners = (): EvidenceSourceOpeners => {
  const setViewingSource = useSetAtom(viewingSourceAtom);
  const setPreviewState = useSetAtom(documentPreviewAtom);
  return useMemo<EvidenceSourceOpeners>(
    () => ({
      openInViewer: (source) => setViewingSource(source),
      openLibraryDocument: (documentId) => setPreviewState({ isOpen: true, documentId }),
      openInNewTab: (url) => window.open(url, '_blank', 'noopener,noreferrer'),
      trackSourceClick: (props) =>
        track({
          event_name: TrackingEventName.EvidenceSourceClicked,
          props,
          options: { amplitude: false },
        }),
      isAuthenticatedUrl: isAuthenticatedSourceUrl,
    }),
    [setViewingSource, setPreviewState]
  );
};

/** Builds the bundle. Memoised, which is why `isPublicRoute` is a thunk. */
export const useEvidenceAnswerPortsValue = (): EvidenceAnswerPorts => {
  const router = useRouter();
  const openers = useEvidenceSourceOpeners();
  const { baseProps } = useEvidenceAnalytics();

  return useMemo<EvidenceAnswerPorts>(() => {
    const chatPorts = resolveEvidenceAnswerChatPorts();
    return {
      ...openers,
      isPublicRoute: () =>
        PUBLIC_ROUTES.some((route) => router.pathname.startsWith(route)),
      ocrQualityTag: CitationOcrQualityTag,
      getPartnerConfig: chatPorts.getPartnerConfig,
      sourceActionsComponent: chatPorts.sourceActionsComponent,
      trackAnswerEvent: (event: EvidenceAnswerEvent) => {
        switch (event.type) {
          case 'source_selected':
            // Generic object_action event, kept in lockstep with
            // `evidence_source_clicked` for the button-clicked dashboard.
            track({
              event_name: TrackingEventName.ButtonClicked,
              props: {
                action: ButtonAction.EvidenceSourceSelected,
                label: 'evidence_source',
                feature: 'evidence',
                source_url: event.source_url,
                source_domain: event.source_domain,
                source_title: event.source_title,
              },
            });
            return;
          case 'reference_scroll_clicked':
            track({
              event_name: TrackingEventName.EvidenceReferenceScrollClicked,
              props: {
                ...baseProps,
                reference_index: event.reference_index,
                source_url: event.source_url,
                source_domain: event.source_domain,
                scroll_succeeded: event.scroll_succeeded,
                chat_id: event.chat_id,
                message_id: event.message_id,
                source_type: event.source_type,
                source_title: event.source_title,
                collections: event.collections,
              },
              options: { amplitude: false },
            });
            return;
          case 'references_section_toggled':
            track({
              options: { amplitude: false },
              event_name: TrackingEventName.EvidenceReferencesSectionToggled,
              props: {
                ...baseProps,
                is_expanded: event.is_expanded,
                references_count: event.references_count,
                trigger: event.trigger,
                chat_id: event.chat_id,
                message_id: event.message_id,
              },
            });
            return;
          case 'references_panel_toggled':
            track({
              event_name: event.is_expanded
                ? TrackingEventName.PanelOpened
                : TrackingEventName.PanelClosed,
              props: {
                ...baseProps,
                panel_type: event.panel_type,
                trigger: event.trigger,
                feature: event.feature,
                references_count: event.references_count,
                chat_id: event.chat_id,
              },
            });
        }
      },
      markSourceRead: (url, title) => void markSourceReadByUrl(url, title),
      printDocument: (url, context) => void triggerBrowserPrint(url, context),
      resolvePartnerFigureUrl: getPartnerFigureUrl,
    };
  }, [router, openers, baseProps]);
};
